// File: stacks/workout/app/src/components/ui/combobox.js
"use client";

import * as React from "react";
import { ChevronsUpDown, Check, X } from "lucide-react";
import { cn } from "lib/utils";

export function Combobox({
  options = [],
  value,
  onChange,
  placeholder = "Select...",
  multiple = false,
  renderChips = false,
  className,
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  // New state to track the highlighted item for keyboard navigation
  const [activeIndex, setActiveIndex] = React.useState(-1);

  // Refs for managing focus and scrolling
  const containerRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const listRef = React.useRef(null);

  const normalized = React.useMemo(() => {
    if (!multiple) return value ?? "";
    return Array.isArray(value) ? value : value ? [value] : [];
  }, [value, multiple]);

  const toggleValue = (v) => {
    if (!multiple) {
      onChange?.(v);
      setOpen(false);
      return;
    }
    const set = new Set(normalized);
    if (set.has(v)) set.delete(v);
    else set.add(v);
    onChange?.(Array.from(set));
    // Clear search after selection in multi-mode for better UX
    setSearch("");
  };

  // Close the popover if the user clicks outside of it
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const categorizedOptions = React.useMemo(() => {
    if (options.length > 0 && options[0].options && Array.isArray(options[0].options)) {
      return options;
    }
    if (options.length > 0 && typeof options[0].value !== "undefined") {
      return [{ label: "Options", options: options }];
    }
    return [];
  }, [options]);
  
  const allFlatOptions = React.useMemo(() => categorizedOptions.flatMap((group) => group.options), [categorizedOptions]);

  const selectedLabels = React.useMemo(() => {
    if (!multiple) {
      const found = allFlatOptions.find((o) => o.value === normalized);
      return found?.label || "";
    }
    const set = new Set(normalized);
    return allFlatOptions.filter((o) => set.has(o.value)).map((o) => o.label);
  }, [normalized, allFlatOptions, multiple]);

  // When opening, reset search and active index for a fresh start
  React.useEffect(() => {
    if (open) {
      setActiveIndex(-1);
    } else {
      // When closing, clear the search term
      setSearch("");
    }
  }, [open]);

  const filteredGroups = React.useMemo(() => {
    if (!search) return categorizedOptions;
    return categorizedOptions
      .map((group) => ({
        ...group,
        options: group.options.filter((opt) =>
          opt.label.toLowerCase().includes(search.toLowerCase())
        ),
      }))
      .filter((group) => group.options.length > 0);
  }, [search, categorizedOptions]);

  // A flattened list of only the currently visible (filtered) options for keyboard navigation
  const flatFilteredOptions = React.useMemo(
    () => filteredGroups.flatMap(group => group.options),
    [filteredGroups]
  );
  
  // Reset active index when search changes
  React.useEffect(() => {
    setActiveIndex(-1);
  }, [search]);
  
  // Scroll the highlighted item into view
  React.useEffect(() => {
    if (activeIndex === -1 || !listRef.current) return;
    const activeItem = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    if (activeItem) {
      activeItem.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex]);

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex(prev => (prev + 1) % flatFilteredOptions.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex(prev => (prev - 1 + flatFilteredOptions.length) % flatFilteredOptions.length);
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0) {
          const selectedOption = flatFilteredOptions[activeIndex];
          toggleValue(selectedOption.value);
          if (!multiple) {
            setOpen(false);
          }
        }
        break;
      case "Escape":
        e.preventDefault();
        setOpen(false);
        break;
      default:
        break;
    }
  };
  
  // With chips, the chips are the selection; the input stays a search box
  // rather than repeating the same list as comma-separated text.
  const displayValue = multiple
    ? renderChips
      ? ""
      : selectedLabels.join(", ")
    : selectedLabels;

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div
        className={cn(
          "flex w-full items-center rounded-md border border-input bg-background text-sm",
          "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring"
        )}
      >
        <input
          ref={inputRef}
          type="text"
          value={open ? search : displayValue}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={displayValue ? undefined : placeholder}
          className={cn(
            "w-full bg-transparent px-3 py-2 outline-none",
            !displayValue && "placeholder:text-text-muted"
          )}
        />
        <button type="button" onClick={() => setOpen(o => !o)} className="px-2">
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </button>
      </div>

      {open && (
        <div ref={listRef} className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover p-1 shadow-md">
          {filteredGroups.length > 0 ? (
            (() => {
              let itemIndex = -1;
              return filteredGroups.map((group) => (
                <div key={group.label}>
                  <div className="px-2 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wider">
                    {group.label}
                  </div>
                  {group.options.map((opt) => {
                    itemIndex++;
                    const currentIndex = itemIndex;
                    const isSelected = multiple
                      ? normalized.includes(opt.value)
                      : normalized === opt.value;
                    const isHighlighted = activeIndex === currentIndex;

                    return (
                      <button
                        key={opt.value}
                        type="button"
                        data-index={currentIndex}
                        className={cn(
                          "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm",
                          isHighlighted ? "bg-accent" : "hover:bg-accent/50"
                        )}
                        onClick={() => toggleValue(opt.value)}
                        onMouseEnter={() => setActiveIndex(currentIndex)}
                      >
                        <Check
                          className={cn(
                            "h-4 w-4",
                            isSelected ? "opacity-100" : "opacity-0"
                          )}
                        />
                        <span>{opt.label}</span>
                      </button>
                    );
                  })}
                </div>
              ));
            })()
          ) : (
            <div className="px-2 py-1.5 text-sm text-text-muted">
              No results found.
            </div>
          )}
        </div>
      )}

      {multiple &&
        renderChips &&
        Array.isArray(normalized) &&
        normalized.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-2">
            {normalized.map((v) => {
              const label = allFlatOptions.find((o) => o.value === v)?.label || v;
              return (
                <span
                  key={v}
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs text-text-base"
                >
                  {label}
                  <button
                    type="button"
                    className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded hover:bg-accent/50"
                    aria-label={`Remove ${label}`}
                    onClick={() => toggleValue(v)}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
    </div>
  );
}

export default Combobox;